/**
 * Multi-Factor Authentication Type Definitions
 * Supports SMS, Email, and TOTP (Google Authenticator) factors
 */

export type FactorType = 'sms' | 'email' | 'totp'

export type FactorStatus = 'ACTIVE' | 'PENDING_ACTIVATION' | 'INACTIVE'

/**
 * Profile information for each factor type
 */
export interface SmsProfile {
  phoneNumber: string
}

export interface EmailProfile {
  email: string
}

export interface TotpProfile {
  credentialId: string
}

export type FactorProfile = SmsProfile | EmailProfile | TotpProfile

/**
 * MFA Factor representation
 */
export interface MfaFactor {
  id: string
  type: FactorType
  status: FactorStatus
  profile: FactorProfile
  isRequired: boolean
  enrolledAt?: string
  lastUsedAt?: string
}

/**
 * User's complete MFA enrollment information
 */
export interface MfaEnrollment {
  userId: string
  factors: MfaFactor[]
  preferredFactorId?: string
}

/**
 * Request/Response types for API calls
 */

// Enroll new factor request
export interface EnrollFactorRequest {
  factorType: FactorType
  phoneNumber?: string // for SMS
  email?: string // for email
}

// Enroll SMS factor response
export interface EnrollOtpFactorResponse {
  factorId: string
  status: FactorStatus
  message: string
  expiresAt: string
}

// Enroll TOTP factor response
export interface EnrollTotpFactorResponse {
  factorId: string
  status: FactorStatus
  qrCode: string // base64 encoded image
  secret: string
  issuer: string
}

export type EnrollFactorResponse =
  | EnrollOtpFactorResponse
  | EnrollTotpFactorResponse

// Activate factor request
export interface ActivateFactorRequest {
  passCode: string
}

// Activate factor response
export interface ActivateFactorResponse {
  factorId: string
  status: FactorStatus
  message: string
}

// Update factor request
export interface UpdateFactorRequest {
  phoneNumber?: string // for SMS
  email?: string // for email
}

// Verify factor during login
export interface VerifyFactorRequest {
  stateToken: string
  factorId: string
  passCode: string
  rememberDevice: boolean
}

export interface VerifyFactorResponse {
  sessionToken: string
  expiresAt: string
}

// Challenge factor request
export interface ChallengeFactorRequest {
  stateToken: string
}

export interface ChallengeFactorResponse {
  factorId: string
  challengeType: FactorType
  message: string
  expiresAt: string
}

// Resend verification code response
export interface ResendCodeResponse {
  message: string
  expiresAt: string
}

// Error responses
export interface MfaErrorResponse {
  error: string
  message: string
  remainingAttempts?: number
  retryAfter?: number
}

/**
 * Factor metadata for UI display
 */
export interface FactorMetadata {
  type: FactorType
  displayName: string
  description: string
  icon: string
  isRequired: boolean
  requiresInput: boolean // true for SMS/Email, false for TOTP during enrollment
}

/**
 * Constants for factor types
 */
export const FACTOR_TYPES: Record<FactorType, FactorMetadata> = {
  sms: {
    type: 'sms',
    displayName: 'SMS',
    description: 'Receive codes via text message',
    icon: 'sms',
    isRequired: true,
    requiresInput: true,
  },
  email: {
    type: 'email',
    displayName: 'Email',
    description: 'Receive codes via email',
    icon: 'email',
    isRequired: false,
    requiresInput: true,
  },
  totp: {
    type: 'totp',
    displayName: 'Authenticator App',
    description: 'Use Google Authenticator or similar app',
    icon: 'smartphone',
    isRequired: false,
    requiresInput: false,
  },
}

/**
 * Helper functions
 */
export const getFactorDisplayName = (type: FactorType): string => {
  return FACTOR_TYPES[type]?.displayName || type
}

export const getFactorDescription = (type: FactorType): string => {
  return FACTOR_TYPES[type]?.description || ''
}

export const isFactorRequired = (type: FactorType): boolean => {
  return FACTOR_TYPES[type]?.isRequired || false
}

export const getMaskedIdentifier = (factor: MfaFactor): string => {
  switch (factor.type) {
    case 'sms':
      const phone = (factor.profile as SmsProfile).phoneNumber
      return phone ? `***-***-${phone.slice(-4)}` : 'Phone'
    case 'email':
      const email = (factor.profile as EmailProfile).email
      if (!email) return 'Email'
      const [localPart, domain] = email.split('@')
      return `${localPart.slice(0, 2)}***@${domain}`
    case 'totp':
      return 'Authenticator App'
    default:
      return 'Unknown'
  }
}

export const getFactorIdentifier = (factor: MfaFactor): string => {
  switch (factor.type) {
    case 'sms':
      return (factor.profile as SmsProfile).phoneNumber || 'Phone'
    case 'email':
      return (factor.profile as EmailProfile).email || 'Email'
    case 'totp':
      return 'Authenticator App'
    default:
      return 'Unknown'
  }
}
